import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LogOut, MoreVertical, Trash2, Inbox, Mail, CheckCircle2, Loader2 } from "lucide-react";

const STATUS_STYLES = {
  new: "bg-primary/15 text-primary border-primary/30",
  read: "bg-white/10 text-foreground border-white/20",
  responded: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  archived: "bg-muted text-muted-foreground border-border",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, responded: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const [inq, st] = await Promise.all([api.get("/inquiries"), api.get("/inquiries/stats")]);
      setInquiries(inq.data);
      setStats(st.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}?status=${status}`);
      toast.success(`Marked as ${status}.`);
      load();
    } catch (e) {
      toast.error("Failed to update status.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/inquiries/${id}`);
      toast.success("Inquiry deleted.");
      setSelected(null);
      load();
    } catch (e) {
      toast.error("Failed to delete inquiry.");
    }
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const cards = [
    { label: "Total Inquiries", value: stats.total, icon: Inbox },
    { label: "New / Unread", value: stats.new, icon: Mail },
    { label: "Responded", value: stats.responded, icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <div className="grain" />
      {/* Top bar */}
      <header className="border-b border-border sticky top-0 bg-black/70 backdrop-blur-xl z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-8 w-8 bg-primary flex items-center justify-center font-display font-bold text-primary-foreground">T</span>
            <div>
              <div className="font-display font-semibold leading-none">Inquiry Console</div>
              <div className="font-mono text-[10px] tracking-widest text-muted-foreground mt-1">{user?.email}</div>
            </div>
          </div>
          <Button onClick={doLogout} data-testid="logout-btn" variant="outline" className="rounded-none border-border hover:border-primary hover:text-primary">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight mb-8">Inquiries</h1>

        {/* Stat cards */}
        <div className="grid sm:grid-cols-3 gap-5 mb-10">
          {cards.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="border border-border bg-card p-6 flex items-center justify-between"
              data-testid={`stat-card-${i}`}
            >
              <div>
                <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase">{c.label}</div>
                <div className="font-display text-4xl font-semibold mt-2">{c.value}</div>
              </div>
              <c.icon className="w-8 h-8 text-primary/60" />
            </motion.div>
          ))}
        </div>

        {/* Table */}
        <div className="border border-border bg-card">
          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : inquiries.length === 0 ? (
            <div className="py-24 text-center text-muted-foreground" data-testid="empty-state">
              <Inbox className="w-10 h-10 mx-auto mb-4 opacity-50" />
              No inquiries yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="font-mono text-xs tracking-widest uppercase">Name</TableHead>
                  <TableHead className="font-mono text-xs tracking-widest uppercase">Contact</TableHead>
                  <TableHead className="font-mono text-xs tracking-widest uppercase hidden md:table-cell">Service</TableHead>
                  <TableHead className="font-mono text-xs tracking-widest uppercase">Status</TableHead>
                  <TableHead className="font-mono text-xs tracking-widest uppercase hidden lg:table-cell">Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.map((inq) => (
                  <TableRow key={inq._id} className="border-border cursor-pointer" onClick={() => setSelected(inq)} data-testid={`inquiry-row-${inq._id}`}>
                    <TableCell className="font-medium">{inq.name}{inq.company ? <span className="block text-xs text-muted-foreground">{inq.company}</span> : null}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{inq.email}{inq.phone ? <span className="block">{inq.phone}</span> : null}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{inq.service || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-none ${STATUS_STYLES[inq.status] || ""}`}>{inq.status}</Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground font-mono">{new Date(inq.created_at).toLocaleDateString()}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-none h-8 w-8" data-testid={`row-menu-${inq._id}`}><MoreVertical className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-none">
                          <DropdownMenuItem onClick={() => changeStatus(inq._id, "read")}>Mark Read</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => changeStatus(inq._id, "responded")}>Mark Responded</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => changeStatus(inq._id, "archived")}>Archive</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Detail dialog */}
      <AlertDialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <AlertDialogContent className="rounded-none border-border bg-card max-w-lg" data-testid="inquiry-detail">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{selected?.name}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-left space-y-3 pt-2 text-sm text-foreground/90">
                <p><span className="text-muted-foreground font-mono text-xs">EMAIL</span><br />{selected?.email}</p>
                {selected?.phone && <p><span className="text-muted-foreground font-mono text-xs">PHONE</span><br />{selected?.phone}</p>}
                {selected?.company && <p><span className="text-muted-foreground font-mono text-xs">COMPANY</span><br />{selected?.company}</p>}
                {selected?.service && <p><span className="text-muted-foreground font-mono text-xs">SERVICE</span><br />{selected?.service}</p>}
                <p><span className="text-muted-foreground font-mono text-xs">MESSAGE</span><br />{selected?.message}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="rounded-none" data-testid="delete-inquiry-btn"><Trash2 className="w-4 h-4 mr-2" /> Delete</Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-none border-border bg-card">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                  <AlertDialogAction className="rounded-none bg-destructive" onClick={() => remove(selected._id)} data-testid="confirm-delete">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <AlertDialogCancel className="rounded-none" data-testid="close-detail">Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
